/**
 * Chef de centre — ce qu'il peut attribuer aux comptes de son centre
 * (décision 449, conception S1097). Règles pures, partagées entre l'API —
 * qui refuse — et l'écran « Comptes de mon centre » — qui ne propose que
 * ce qui passera.
 *
 * Deux bornes, qui se cumulent :
 *   1. une liste FERMÉE de rôles, par leur code ;
 *   2. les permissions EFFECTIVES de ce rôle, lues au moment du geste : un
 *      natif personnalisé qui aurait reçu un droit d'administration sort de
 *      la liste de lui-même, sans qu'on pense à la mettre à jour.
 */
import { CODE_CHEF_CENTRE, CODE_MEDECIN_CHEF_CENTRE, CODES_CHEF_DE_CENTRE, PERMISSIONS, } from '../constants/roles.js';
import { memePrestation } from './boxes.js';
import { exercePrerogativesDeSite } from './responsabilite.js';
import { ROLE_EN_BOX, prestationDeLigne } from './sites.js';
/**
 * D3 (TL, 02/11) : agent d'accueil, médecin — le dentiste en est un, par sa
 * spécialité — et « Accueil + caisse ». Jamais un autre chef, ni un rôle de
 * la pharmacie, de la caisse seule ou de la PEC.
 */
export const ROLES_CREABLES_PAR_LE_CHEF = [
    'AGENT_ACCUEIL',
    'MEDECIN',
    'ACCUEIL_CAISSE',
];
/**
 * D16 : ce qu'aucun compte créé par un chef ne porte. `utilisateur:*` et
 * `role:*` se lisent par préfixe, pour qu'une permission de comptes ou de
 * rôles ajoutée plus tard y tombe d'office — `utilisateur:write_centre`
 * comprise : un chef ne fait jamais un autre chef. `export:data` (l'export
 * de masse) aussi : défense si un natif créable est un jour personnalisé
 * (complément 449, S1105) ; de même `registre:export_entreprise` (décision 458).
 */
export const PERMISSIONS_INTERDITES_AU_CHEF = PERMISSIONS.filter((permission) => permission.startsWith('utilisateur:') ||
    permission.startsWith('role:') ||
    permission === 'site:write' ||
    permission === 'configuration:write' ||
    permission === 'audit:read' ||
    permission === 'sauvegarde:telecharger' ||
    permission === 'export:data' ||
    // Décision 458 : le registre d'une entreprise sur un mois, tous centres.
    permission === 'registre:export_entreprise');
export const MOTIF_ROLE_NON_CREABLE_PAR_LE_CHEF = 'Ce rôle ne s’attribue pas depuis « Mon centre » : demandez-le à la Direction.';
/**
 * Le chef peut-il attribuer ce rôle ? Oui si son code est dans la liste et
 * si ses permissions effectives ne contiennent rien d'interdit.
 */
export function roleCreableParLeChef(role) {
    return (ROLES_CREABLES_PAR_LE_CHEF.includes(role.code) &&
        !role.permissions.some((permission) => PERMISSIONS_INTERDITES_AU_CHEF.includes(permission)));
}
// ─────────────────────────────────────────────────────────────────────
//  « Mon centre » (PR 3) — les gestes du chef sur les comptes de son site
// ─────────────────────────────────────────────────────────────────────
export const MOTIF_AUCUN_CENTRE = 'Vous n’êtes responsable d’aucune antenne.';
export const MOTIF_SITE_IMPOSE = 'Le centre d’un compte ne se choisit pas ici : c’est le vôtre. Pour un autre centre, demandez à la Direction.';
export const MOTIF_REACTIVATION_DIRECTION = 'Un compte désactivé ne se réactive pas depuis « Mon centre » : demandez-le à la Direction.';
export const MOTIF_SON_PROPRE_COMPTE = 'Pour votre propre compte, passez par « Mon compte ».';
export const MOTIF_COMPTE_NON_GERABLE = 'Ce compte ne se gère pas depuis « Mon centre » : demandez-le à la Direction.';
/**
 * D4 : les spécialités demandées qui ne sont pas dans la dotation du centre.
 * Vide : tout est permis. La comparaison ignore la casse, comme les box.
 */
export function specialitesHorsDotation(specialites, dotation) {
    return specialites.filter((voulue) => !dotation.some((prevue) => memePrestation(prevue, voulue)));
}
export const MOTIF_METIER_ABSENT = 'Ce centre n’a pas de box ni de dotation pour ce métier.';
export function metiersDuCentre(centre) {
    const prevues = centre.lignes.filter((ligne) => ligne.effectifDeclare > 0);
    const specialites = [];
    const ajouter = (prestation) => {
        const rangee = prestation.trim().toUpperCase();
        if (rangee !== '' && !specialites.some((s) => memePrestation(s, rangee))) {
            specialites.push(rangee);
        }
    };
    for (const ligne of prevues)
        if (ligne.role === ROLE_EN_BOX)
            ajouter(prestationDeLigne(ligne));
    for (const box of centre.box)
        ajouter(box.prestation);
    return {
        accueil: prevues.some((ligne) => ligne.role === 'AGENT_ACCUEIL'),
        specialites: specialites.sort(),
    };
}
/** Le métier de ce rôle de la liste existe-t-il dans le centre ? */
export function metierPresent(code, metiers) {
    if (code === 'AGENT_ACCUEIL' || code === 'ACCUEIL_CAISSE')
        return metiers.accueil;
    if (code === ROLE_EN_BOX)
        return metiers.specialites.length > 0;
    return false;
}
// ─────────────────────────────────────────────────────────────────────
//  Désignation du chef de centre (PR 2) — `PUT /sites/:id/chef-de-centre`
// ─────────────────────────────────────────────────────────────────────
export const MOTIF_CHEF_BASE_REFUSEE = 'Seul un médecin ou un compte « Responsable d’antenne » peut être désigné responsable d’antenne.';
export const MOTIF_CHEF_AUTRE_SITE = 'Ce compte n’est pas rattaché à ce centre.';
export const MOTIF_CHEF_INACTIF = 'Ce compte est désactivé.';
export const MOTIF_CHEF_DEJA_AILLEURS = 'Ce compte est déjà responsable d’une autre antenne.';
export const MOTIF_CHEF_MEDECIN_DERIVE = 'Ce médecin a un rôle dérivé : repassez-le d’abord au rôle « Médecin » dans Utilisateurs, puis désignez-le.';
export const MOTIF_CHEF_PAR_DESIGNATION = 'Ce rôle ne s’attribue que par la désignation sur le site : Sites → Responsable d’antenne.';
/**
 * N9 de S1117 : « Médecin + chef de centre » ne se donne que par la
 * désignation, jamais par Utilisateurs. CHEF_CENTRE, lui, s'attribue : la
 * Direction crée le compte avant de le désigner.
 */
export function seDonneParDesignation(code) {
    return code === CODE_MEDECIN_CHEF_CENTRE;
}
/**
 * Le rôle que prend le compte désigné (D12 révisée) : un « Médecin » devient
 * « Médecin + chef de centre » ; un « Chef de centre » le reste. `null` pour
 * tout autre compte :
 *   - un dérivé libre de la base CHEF_CENTRE : il ne porte pas
 *     `utilisateur:write_centre` (RG-SEC-ROLE-CENTRE) et n'ouvrirait rien ;
 *   - un médecin d'un rôle dérivé (N6 de S1117, tranché par l'orchestrateur) :
 *     retiré, il repasserait MEDECIN et perdrait son dérivé. On ne mémorise
 *     pas l'ancien code : on refuse (`motifDuChefRefuse`).
 */
export function roleCodeDuChefDesigne(compte) {
    const code = compte.roleCode ?? compte.role;
    if (code === 'MEDECIN')
        return CODE_MEDECIN_CHEF_CENTRE;
    if (code === CODE_CHEF_CENTRE)
        return CODE_CHEF_CENTRE;
    return null;
}
/** Le motif du refus RG-SEC-CENTRE quand `roleCodeDuChefDesigne` rend `null`. */
export function motifDuChefRefuse(compte) {
    return compte.role === 'MEDECIN' ? MOTIF_CHEF_MEDECIN_DERIVE : MOTIF_CHEF_BASE_REFUSEE;
}
/**
 * Le rôle que garde l'ancien chef (D15 révisée) : le médecin repasse
 * MEDECIN ; un « Chef de centre » garde son rôle, qui n'ouvre plus rien sans
 * désignation (`siteDuChef`).
 */
export function roleCodeApresRetrait(compte) {
    const code = compte.roleCode ?? compte.role;
    return code === CODE_MEDECIN_CHEF_CENTRE ? 'MEDECIN' : code;
}
/**
 * Le centre dont l'acteur est le chef, maintenant — ou `null`.
 *
 * Trois conditions, toutes exigées (D13, D8, condition N1 de la revue S1105) :
 *   1. le rôle de chef : l'un des deux natifs, par son code ;
 *   2. la désignation : `Site.responsableId`, lue en base par l'appelant ;
 *   3. la session ouverte sur ce site.
 *
 * Le Super administrateur détient `utilisateur:write_centre` par calcul
 * (décision 418) mais n'a pas le code d'un chef, et ne peut pas être
 * désigné : il n'a donc aucun centre. La permission seule n'ouvre rien.
 */
export function siteDuChef(acteur, sites) {
    if (!CODES_CHEF_DE_CENTRE.includes(acteur.roleCode))
        return null;
    const designes = sites.filter((site) => site.actif && site.responsableId === acteur.id);
    const contexte = {
        siteSession: acteur.siteId,
        sitesSousResponsabilite: designes.map((site) => site.id),
    };
    return designes.find((site) => exercePrerogativesDeSite(contexte, site.id))?.id ?? null;
}
/**
 * Revue S1112, complément 449 de S1131 : un « Chef de centre » non médecin
 * qui n'est chef d'aucun centre n'ouvre RIEN — ni la file, ni le registre,
 * ni la caisse et ses reçus, ni les box, ni les écrans d'appel, ni « Mon
 * centre ». Seule la lecture du référentiel des sites lui reste (choisir son
 * poste, lire le nom de son centre). La règle se lit à l'envers, par la seule
 * permission gardée : une permission ajoutée un jour à la base CHEF_CENTRE,
 * ou à un dérivé de cette base, exige d'office la désignation.
 */
export const PERMISSIONS_DU_CHEF_SANS_CENTRE = ['site:read'];
/** Cette permission exige-t-elle, pour ce rôle de base, `siteDuChef` non nul ? */
export function exigeLaDesignation(role, permission) {
    return role === CODE_CHEF_CENTRE && !PERMISSIONS_DU_CHEF_SANS_CENTRE.includes(permission);
}
//# sourceMappingURL=chef-de-centre.js.map